import CallToActionButton from '@/components/Common/CallToActionButton/CallToActionButton';
import Popup from '@/components/Common/DesignSystem/Popup/Popup';
import WidgetNotification from '@/components/Train/SHome/WidgetNotification/WidgetNotification';
import { Braze, Kinesis } from '@/services/TrackerService';
import { TrainSHomeStoreCtx } from '@/stores/TrainStore/TrainSHomeStore';
import { getDateString } from '@/utils/domain/train';
import useModalHash from '@/utils/hooks/useModalHash';
import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import { pipe } from 'ramda';
import React, { useContext, useEffect } from 'react';

import TrainSHomeJourneyType from './JourneyType/TrainSHomeJourneyType';
import TrainSHomeFilterSection from './Section/TrainSHomeFilterSection';
import TrainSHomeStation from './Station/TrainSHomeStation';
import styles from './TrainSHomeBody.module.scss';

const TrainSHomeStationModal = dynamic(() => import('./StationModal/TrainSHomeStationModal'), { ssr: false });
const TrainSHomeCapacityModal = dynamic(() => import('./CapacityModal/TrainSHomeCapacityModal'), { ssr: false });
const TrainSHomeCalendarModal = dynamic(
  () => import('@/components/Train/SHome/CalendarModal/TrainSHomeCalendarModal'),
  { ssr: false },
);

const TrainSHomeBody: React.FC = () => {
  const store = useContext(TrainSHomeStoreCtx);
  const [isOpenStationModal, , isStationModalInitialLoad] = useModalHash({ id: store.trainStationHashKey });
  const [isOpenCalendarModal, , isCalendarModalInitialLoad] = useModalHash({ id: store.trainDateHashKey });
  const [isOpenCapacityModal, , isCapacityModalInitialLoad] = useModalHash({ id: store.trainCapacityHashKey });

  const dateLabel = store.IsRoundTrip ? '가는 날 - 오는 날' : '가는 날';
  const datePlaceholder = <div className={styles.datePlaceholder}>날짜를 선택해 주세요.</div>;

  const dateString = store.IsRoundTrip
    ? getDateString(store.departureDate, store.returnDate)
    : getDateString(store.departureDate, null);

  useEffect(() => {
    Kinesis.view('TrainHome');
  }, []);

  const saveInStore = (): void => {
    store.cleanStorage();
    store.saveCurrentSearchConditionToSessionStorage();
  };

  const trackSubmitSearch = (): void => {
    if (store.IsActive) {
      saveInStore();

      const tripType = store.IsRoundTrip ? 'round' : 'one';

      const departureTrainMeta = store.getTrainMeta('departTrain', tripType);

      const trainMeta = store.IsRoundTrip
        ? [departureTrainMeta, store.getTrainMeta('returnTrain', tripType)]
        : [departureTrainMeta];

      Kinesis.click('TrainHome', {
        object: 'trainSearch',
        trainMeta,
      });

      const [trainMetaFrom, trainMetaTo] = trainMeta;
      const outboundDate = store.departureDate?.toISO({ suppressMilliseconds: true });
      const returnDate = store.returnDate?.toISO({ suppressMilliseconds: true });

      Braze.sendCustomEvent('click_trans_detail', {
        outbound_departure_no: trainMetaFrom.departureStationCode, // from 출발지 코드
        outbound_arrival_no: trainMetaFrom.arrivalStationCode, // from 도착지 코드
        return_departure_no: trainMetaTo?.departureStationCode, // to 출발지 코드
        return_arrival_no: trainMetaTo?.arrivalStationCode, // to 도착지 코드
        outbound_departure_name: trainMetaFrom.departureStation, // from 출발지 이름
        outbound_arrival_name: trainMetaFrom.arrivalStation, // from 도착지 이름
        return_departure_name: trainMetaTo?.departureStation, // to 출발지 이름
        return_arrival_name: trainMetaTo?.arrivalStation, // to 도착지 이름
        isRoundTrip: store.IsRoundTrip, // 왕복 여부
        category_id: '10301', // 카테고리 ID
        category_name: '기차', // 카테고리 이름
        outbound_departure_date: outboundDate, // from 출발일
        outbound_arrival_date: outboundDate, // from 도착일
        return_departure_date: returnDate, // to 출발일
        return_arrival_date: returnDate, // to 도착일
      });
    }
  };

  const renderSearchForm = (): JSX.Element => (
    <section className={styles.searchForm}>
      <TrainSHomeJourneyType />
      <section className={styles.searchBody}>
        <TrainSHomeStation />
        <TrainSHomeFilterSection label={dateLabel} onClick={store.onOpenCalendarModal}>
          {dateString || datePlaceholder}
        </TrainSHomeFilterSection>
        <TrainSHomeFilterSection label="인원" onClick={store.onOpenCapacityModal}>
          {store.CapacityString}
        </TrainSHomeFilterSection>
        <CallToActionButton
          type="static"
          className={styles.submit}
          disabled={!store.IsActive}
          onClick={pipe(trackSubmitSearch, store.submit)}
        >
          승차권 조회
        </CallToActionButton>
      </section>
    </section>
  );

  return (
    <section className={styles.container}>
      {store.widgetNotification?.message && (
        <WidgetNotification message={store.widgetNotification.message} className={styles.widgetNotification} />
      )}
      {renderSearchForm()}
      {isStationModalInitialLoad && <TrainSHomeStationModal isOpenStationModal={isOpenStationModal} />}
      {isCapacityModalInitialLoad && (
        <TrainSHomeCapacityModal
          adultCount={store.passengerCountAdult}
          childrenCount={store.passengerCountChild}
          minCapacity={store.Capacity.min}
          maxCapacity={store.Capacity.max}
          isOpenModal={isOpenCapacityModal}
          onCloseModal={store.onCloseCapacityModal}
          onSubmit={store.onChooseCapacity}
        />
      )}
      {isCalendarModalInitialLoad && <TrainSHomeCalendarModal isOpenCalendarModal={isOpenCalendarModal} />}
      <Popup
        isOpen={store.isOpenAlert}
        mainBtn={{
          label: '확인',
          onClick: store.onCloseAlert,
        }}
      >
        {store.alertType === 'NOTAVAILABLE' ? (
          <>
            승차권 예약은 <br />
            {store.AvailableStartDateString} 부터 선택 가능합니다.
          </>
        ) : (
          { title: '출발역과 도착역이 같아요', description: '출발역 또는 도착역을 변경해 주세요.' }
        )}
      </Popup>
    </section>
  );
};

export default observer(TrainSHomeBody);
